'use client';

import { useState, useEffect, useSyncExternalStore, useCallback } from 'react';

// Singleton timer manager to avoid multiple intervals
let currentTime = Date.now();
let listeners = new Set<() => void>();
let intervalId: ReturnType<typeof setInterval> | null = null;

function startTimer() {
  if (intervalId === null && listeners.size > 0) {
    intervalId = setInterval(() => {
      currentTime = Date.now();
      listeners.forEach((listener) => listener());
    }, 1000);
  }
}

function stopTimer() {
  if (intervalId !== null && listeners.size === 0) {
    clearInterval(intervalId);
    intervalId = null;
  }
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  startTimer();
  return () => {
    listeners.delete(listener);
    stopTimer();
  };
}

function getSnapshot() {
  return currentTime;
}

/**
 * Hook for components that need current time updates every second.
 * Uses a singleton timer to avoid multiple intervals.
 * Only components using this hook will re-render on time updates.
 */
export function useCurrentTime(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
